import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiTrash, BiUpload } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useAppSelector } from '@/app/hook';
import {
  useDeleteConversationHistoryAllMutation,
  useDeleteConversationHistoryMutation,
  useExportChatbotHistoryMutation,
  useGetConversationHistoryDetailQuery,
  useGetConversationHistoryQuery,
} from '@/app/services/chatbot-rtk.services';
import { BannerHelper } from '@/components/common/BannerToast';
import { CustomReactMarkdown } from '@/components/common/CustomReactMarkdown';
import InformationModal from '@/components/dashboard/knowledge-bases-create/InformationModal';
import { PSButton } from '@/components/ui/button';
import { PSInput } from '@/components/ui/input';
import { selectAppearanceState } from '@/features/appearance/appearanceSlice';
import { ChatbotEditContext } from '@/pages/dashboard/ChatbotEdit';

import { DeleteConfirmation } from '../../common/confirmation/delete-confirmation';
import LoadingComponent from '../../common/loading/loading.component';
import NotShowContentInMobileSize from '../NotShowContentInMobileSize';

function BotEditHistoryMessage() {
  const { t } = useTranslation();
  const chatBotEditValue = useContext(ChatbotEditContext);
  const { configs } = useAppSelector(selectAppearanceState);

  const [page, setPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [limit] = useState(10); // Number of items per page

  const [conversationId, setConversationId] = useState<string>(''); // Conversation

  const { data, isLoading, error } = useGetConversationHistoryQuery({
    botId: chatBotEditValue.value.botId,
    limit,
    page,
  });

  const [exportChatbotHistoryApi, exportChatbotHistoryApiStatus] = useExportChatbotHistoryMutation();

  const [deleteConversationHistory, { isLoading: isDeleteing }] = useDeleteConversationHistoryMutation();

  const [deleteConversationHistoryAll, { isLoading: isDeleteingAll }] = useDeleteConversationHistoryAllMutation();

  const [chatHistoryData, setChatHistoryData] = useState<any[]>([]);

  const getConversationHistoryDetail = useGetConversationHistoryDetailQuery(
    {
      id: conversationId,
      botId: chatBotEditValue.value.botId,
    },
    {
      skip: conversationId == '' ? true : false,
    }
  );

  const totalResults = data?.total || 0; // Total number of results
  const totalPages = Math.ceil(totalResults / limit); // Total pages based on results

  const formatDate = (inputDate: string): string => {
    const date = new Date(inputDate);

    // Format the date as "24 July, 2024"
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });

    // Format the time as "11:38 PM"
    const formattedTime = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    });

    return `${formattedDate} | ${formattedTime}`;
  };

  const convertChatHistoryDetails = (response: any): IChatMessage[] | null => {
    if (!response?.value) return null;

    try {
      const rawData = JSON.parse(response.value)?.history;
      return rawData || null;
    } catch (error) {
      console.error('Error parsing chat history details:', error);
      return null;
    }
  };

  useEffect(() => {
    if (data?.data && data.data.length > 0) {
      const ls = [];
      for (const item of data.data) {
        ls.push({
          ...item,
          selected: false,
        });
      }

      if (conversationId == '') {
        const firstData = data.data[0];
        setConversationId(firstData.id);
      }

      setChatHistoryData(ls);
    } else {
      setChatHistoryData([]);
    }
  }, [data]);

  const changeCheckbox = (index: number) => {
    const ls = [...chatHistoryData];
    ls[index].selected = !ls[index].selected;
    setChatHistoryData(ls);
  };

  const downloadTextFile = (text: string) => {
    // Create a blob from the text
    const blob = new Blob([text], { type: 'text/plain' });
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'conversation.txt';
    // Append the anchor to the document, trigger a click, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportChatbotHistory = () => {
    const ids = getConversationIds();
    if (ids.length == 0) return;

    exportChatbotHistoryApi({
      bot_id: chatBotEditValue.value.botId,
      ids: ids,
    })
      .unwrap()
      .then(data => {
        downloadTextFile(data);
      })
      .catch(err => {
        toast.error(err?.data?.message || err.message);
      });
  };

  const markdownComponents = {
    // Headings
    h1: ({ children }: any) => <h1 className="text-3xl font-bold mb-3">{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-2xl font-bold mb-2">{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-xl font-bold mb-1">{children}</h3>,
    h4: ({ children }: any) => <h4 className="text-lg font-bold mb-1">{children}</h4>,

    // Paragraphs and text formatting
    p: ({ children }: any) => <p className="leading-relaxed">{children}</p>,

    strong: ({ children }: any) => <strong className="font-bold">{children}</strong>,

    em: ({ children }: any) => <em className="italic">{children}</em>,

    // Lists
    ul: ({ children }: any) => <ul className="list-disc pl-4 mb-4">{children}</ul>,
    ol: ({ children }: any) => <ol className="list-decimal pl-4 mb-4">{children}</ol>,
    li: ({ children }: any) => <li className="mb-1">{children}</li>,

    // Blockquotes
    blockquote: ({ children }: any) => <blockquote className="border-l-4 border-gray-300 pl-4 italic my-4">{children}</blockquote>,

    // Links
    a: ({ href, children }: any) => (
      <a href={href} className="text-primary hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),

    // Code blocks
    code: ({ inline, children, ...props }: any) => {
      if (inline) {
        return (
          <code className={`bg-gray-100 text-black rounded p-2 font-mono text-sm`} {...props}>
            {children}
          </code>
        );
      }
      return (
        <code className={`block bg-gray-100 text-black rounded-lg font-mono text-sm overflow-x-scroll p-2`} {...props}>
          {children}
        </code>
      );
    },

    // Pre blocks (usually contains code)
    pre: ({ children }: any) => <pre className="overflow-x-scroll my-4">{children}</pre>,

    // Tables
    table: ({ children }: any) => (
      <div className="overflow-x-scroll my-4">
        <table className="min-w-full divide-y divide-gray-200">{children}</table>
      </div>
    ),

    thead: ({ children }: any) => <thead className="bg-gray-50">{children}</thead>,

    tbody: ({ children }: any) => <tbody className="divide-y divide-gray-200">{children}</tbody>,

    tr: ({ children }: any) => <tr>{children}</tr>,

    td: ({ children }: any) => <td className="px-6 py-4 whitespace-nowrap">{children}</td>,

    th: ({ children }: any) => <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{children}</th>,

    // Horizontal rule
    hr: () => <hr className="my-4 border-t border-gray-300" />,

    // Images
    img: ({ src, alt }: any) => <img src={src} alt={alt} className="max-w-full h-auto rounded-lg my-4" />,
  };

  const getConversationIds = () => {
    const ids = chatHistoryData.filter(dr => dr.selected == true).map(dr => dr.id);

    return ids;
  };

  const onDeleteAllPressed = () => {
    setShowDeleteAllModal(true);
  };
  const onDeletePressed = () => {
    setShowDeleteModal(true);
  };
  const onDeleteConfirmed = () => {
    const ids = getConversationIds();
    if (ids.length == 0) return;

    deleteConversationHistory({
      bot_id: chatBotEditValue.value.botId,
      ids: ids,
    })
      .unwrap()
      .then(() => {
        setShowDeleteModal(false);
      })
      .catch(err => {
        toast.error(err?.data?.message || err.message);
      });
  };
  const onDeleteAllConfirmed = () => {
    deleteConversationHistoryAll({
      bot_id: chatBotEditValue.value.botId,
    })
      .unwrap()
      .then(() => {
        setShowDeleteAllModal(false);
      })
      .catch(err => {
        toast.error(err?.data?.message || err.message);
      });
  };

  const list = useMemo(() => {
    return convertChatHistoryDetails(getConversationHistoryDetail.data);
  }, [getConversationHistoryDetail.data]);

  const trashIcon = <BiTrash className="tw-w-4 tw-h-4 tw-relative tw-overflow-hidden tw-text-white" />;
  return (
    <section>
      <div className="tw-mb-4">
        <BannerHelper
          message={t('HelpBanner/History/hint')}
          buttons={
            <InformationModal
              btnTitle={t('HelpBanner/History/cta')}
              modalTitle={t('HelpBanner/History/title')}
              content={[t('HelpBanner/History/content1'), t('HelpBanner/History/content2'), t('HelpBanner/History/content3'), t('HelpBanner/History/content4')]}
              youtube={{
                enUrl: 'https://www.youtube.com/embed/AOoM3j4eHFU',
                frUrl: 'https://www.youtube.com/embed/AOoM3j4eHFU',
              }}
            />
          }
        />
      </div>
      <div className="tw-hidden lg:tw-grid tw-w-full tw-h-[calc(100svh-150px)] tw-p-6 tw-bg-white tw-rounded-2xl tw-shadow  tw-gap-6  tw-grid-cols-[500px_1fr] ">
        <div className="tw-justify-center tw-items-start tw-gap-6 tw-inline-flex">
          <div className="tw-grow tw-shrink tw-basis-0 tw-self-stretch tw-flex-col tw-justify-between tw-items-start tw-inline-flex">
            <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
              <div className="tw-self-stretch tw-h-20 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
                <div className="tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">{t('ConversationHistory/l-01')}</div>
                <div className="tw-w-full tw-h-8 tw-justify-start tw-items-start tw-gap-4 tw-inline-flex tw-flex-wrap">
                  <PSButton
                    id="btn-upload-chatbot-history"
                    className="gap-2"
                    onClick={exportChatbotHistory}
                    disabled={!chatHistoryData.some(dr => dr.selected == true)}
                    icon={<BiUpload className="tw-w-4 tw-h-4 tw-relative tw-overflow-hidden tw-text-white" />}
                    title={t('ConversationHistory/l-02')}
                  />
                  <PSButton
                    id="btn-delete-hisoty"
                    className="gap-2"
                    disabled={!chatHistoryData.some(dr => dr.selected == true)}
                    onClick={onDeletePressed}
                    icon={trashIcon}
                    title={t('DeleteModal/key1')}
                  />
                  <PSButton
                    id="btn-delete-all-history"
                    className="gap-2"
                    onClick={onDeleteAllPressed}
                    disabled={chatHistoryData.length == 0}
                    icon={trashIcon}
                    title={t('DeleteModal/key3')}
                  />
                </div>
              </div>

              <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-flex tw-h-[calc(100vh-360px)] tw-overflow-y-auto">
                {isLoading && <LoadingComponent />}
                {!isLoading && (
                  <>
                    {chatHistoryData.map((item: any, index: number) => (
                      <div key={index} className="tw-w-full">
                        <div className="tw-flex tw-items-start tw-py-4">
                          <PSInput
                            className="tw-mt-[5px] !tw-w-auto "
                            type="checkbox"
                            checked={item.selected}
                            onChange={() => {
                              changeCheckbox(index);
                            }}
                          />
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setConversationId(item.id);
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                setConversationId(item.id);
                              }
                            }}
                            key={index}
                            className="tw-cursor-pointer tw-self-stretch  tw-border-b tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex"
                          >
                            <div className="tw-grow tw-shrink tw-basis-0 tw-h-11 tw-justify-start tw-items-start tw-gap-2 tw-flex">
                              <div className="tw-h-6 tw-py-1 tw-justify-start tw-items-center tw-gap-2.5 tw-flex">
                                <div className="tw-h-4 tw-relative tw-bg-white tw-rounded tw-border tw-border-gray-300" />
                              </div>
                              <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-inline-flex">
                                <div className="tw-self-stretch tw-text-gray-800 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                                  {t('ConversationHistory/l-08')} {item.id}
                                </div>
                                <div className="tw-w-80 tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                                  {formatDate(item.creation_date_time)} (UTC | +/- 15min)
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <hr className="tw-bg-black tw-w-full tw-m-0" />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
            <div className="tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex tw-mt-3">
              {/* Pagination */}
              <Pagination totalPages={data?.pagination.total_pages || 1} currentPage={page} onPageChange={setPage} />
              <div>
                <span className="tw-text-gray-800 tw-text-sm tw-font-medium tw-font-['Inter'] tw-leading-tight">{t('ConversationHistory/l-03')} </span>
                <span className="tw-text-gray-800 tw-text-sm tw-font-medium tw-font-['Inter'] tw-leading-tight">{data?.pagination?.page || '1'} </span>
                <span className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Inter'] tw-leading-tight">{t('ConversationHistory/l-05')} </span>
                <span className="tw-text-gray-800 tw-text-sm tw-font-medium tw-font-['Inter'] tw-leading-tight">{data?.pagination?.total_pages || '1'}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-300 tw-p-5 tw-max-h-[calc(100svh-160px)]  tw-overflow-y-auto ">
          <>
            {getConversationHistoryDetail.isLoading && <LoadingComponent />}
            {!getConversationHistoryDetail.isLoading && list && list?.length > 0 ? (
              <>
                {list?.map((item, index) => (
                  <div key={index}>
                    {item.role == 'user' && (
                      <div className="tw-w-full  tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                        <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
                          <div className="tw-px-4 tw-py-3 tw-bg-primary tw-rounded-tl-2xl tw-rounded-tr-2xl tw-rounded-br-2xl tw-shadow tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                            <CustomReactMarkdown
                              content={item.content}
                              className="tw-break-words"
                              wrapperClassName="tw-break-words tw-max-w-[400px] tw-text-gray-100 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {item.role == 'assistant' && (
                      <div className="tw-w-full   tw-flex-col tw-justify-start tw-items-end tw-gap-1 tw-inline-flex">
                        <div className="tw-max-w-[400px] tw-px-4 tw-py-3 tw-bg-white tw-rounded-2xl tw-shadow tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                          <CustomReactMarkdown
                            content={item.content}
                            className="tw-break-words"
                            wrapperClassName="tw-break-words tw-grow tw-shrink tw-basis-0 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                          />
                        </div>
                        <img
                          src="https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
                          className="tw-rounded-[99px] tw-object-cover tw-min-w-[16px] tw-min-h-[16px] tw-max-w-[16px] tw-max-h-[16px] tw-mt-1"
                          alt=""
                        />
                      </div>
                    )}
                  </div>
                ))}
              </>
            ) : !list && !configs?.save_conversation ? (
              <div className="tw-text-gray-700 tw-text-l tw-font-regular tw-font-['Lexend'] tw-leading-loose tw-text-center">
                {t('ConversationHistory/NoData')}
              </div>
            ) : (
              <></>
            )}
          </>
        </div>
      </div>
      <NotShowContentInMobileSize title="" description={t('ConversationHistory/l-07')} />
      {showDeleteModal && (
        <DeleteConfirmation
          show={showDeleteModal}
          dismiss={() => {
            setShowDeleteModal(false);
          }}
          title={t('DeleteModal/key4')}
          description={t('DeleteModal/key5')}
          onDelete={onDeleteConfirmed}
          isLoading={isDeleteing}
        />
      )}
      {showDeleteAllModal && (
        <DeleteConfirmation
          show={showDeleteAllModal}
          dismiss={() => {
            setShowDeleteAllModal(false);
          }}
          title={t('DeleteModal/key6')}
          description={t('DeleteModal/key5')}
          deleteText={t('DeleteModal/key3')}
          onDelete={onDeleteAllConfirmed}
          isLoading={isDeleteingAll}
        />
      )}
    </section>
  );
}

export default BotEditHistoryMessage;

type PaginationProps = {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
};

const Pagination: React.FC<PaginationProps> = ({ totalPages, currentPage, onPageChange }) => {
  const getPaginationRange = () => {
    const totalVisibleButtons = 7; // Total buttons, including ellipses and edge pages
    const sideButtons = Math.floor((totalVisibleButtons - 3) / 2); // Buttons on each side of the current page
    const paginationRange = [];

    // Always include the first page
    paginationRange.push(1);

    // Calculate left and right ranges
    const leftBoundary = Math.max(2, currentPage - sideButtons);
    const rightBoundary = Math.min(totalPages - 1, currentPage + sideButtons);

    // Add left ellipsis if needed
    if (leftBoundary > 2) {
      paginationRange.push('...');
    }

    // Add middle range of pages
    for (let i = leftBoundary; i <= rightBoundary; i++) {
      paginationRange.push(i);
    }

    // Add right ellipsis if needed
    if (rightBoundary < totalPages - 1) {
      paginationRange.push('...');
    }

    // Always include the last page
    if (totalPages > 1) {
      paginationRange.push(totalPages);
    }

    return paginationRange;
  };

  const paginationRange = getPaginationRange();

  return (
    <div>
      <ul style={{ listStyleType: 'none', display: 'flex', gap: '5px' }} className="tw-p-0 tw-m-0">
        <>
          {paginationRange.map((page, index) => (
            <React.Fragment key={index}>
              {page == '...' ? (
                <>...</>
              ) : (
                <li key={page}>
                  <button
                    type="button"
                    style={{
                      cursor: 'pointer',
                      padding: '5px 10px',
                      backgroundColor: currentPage === page ? '#007bff' : '#f1f1f1',
                      color: currentPage === page ? '#fff' : '#000',
                      borderRadius: '5px',
                      border: 'none',
                    }}
                    onClick={() => onPageChange(+page)}
                  >
                    {page}
                  </button>
                </li>
              )}
            </React.Fragment>
          ))}
        </>
      </ul>
    </div>
  );
};

interface IChatMessage {
  role: 'user' | 'assistant'; // The role of the message sender (e.g., "user" or "assistant")
  content: string; // The content of the message
  date: string; // The ISO date string representing the time the message was sent
}
